'use client'

import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { fetchFedwatchProbabilities } from '@/service/fedwatch.api'
import {
  buildFedwatchRequest,
  FEDWATCH_EXCHANGE,
  FEDWATCH_SYMBOL,
  fedwatchBucketMsForSpan,
  pickNewerFedwatchList,
  resolveFedwatchRange,
} from '@/service/fedwatch.types'
import { useFedwatchStream } from '@/service/ws/use-fedwatch-stream'
import { AnalyticsType, type Exchange } from '@/types/generated/common/types'
import type { FedwatchProbabilityList } from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

export const fedwatchKeys = {
  all: ['fedwatch'] as const,
  grid: (exchange: Exchange) => [...fedwatchKeys.all, 'grid', exchange] as const,
  timeline: (exchange: Exchange, meetingDate: string, from: string, to: string) =>
    [...fedwatchKeys.all, 'timeline', exchange, meetingDate, from, to] as const,
}

// A 400 means we built a request the handler rejects outright — as_of together with
// from/to, or a timeline without a meeting date. Retrying cannot fix that and only
// triples the noise, so only transport-ish failures get a second look.
const retryUnlessBadRequest = (failureCount: number, error: unknown) =>
  (error as ErrorResponse | undefined)?.status !== 400 && failureCount < 2

/**
 * The latest grid across every upcoming meeting.
 *
 * Polls unconditionally at the producer's own cadence. The poll is deliberately NOT
 * relaxed while a stream subscription is open: subscribing says only that we asked,
 * not that the socket is alive or that any push has landed, so slowing it there
 * would slow it exactly when the socket is dead and the poll is the only thing left.
 * The payload is ~120 rows, so a flat minute is cheap and self-healing.
 */
export function useFedwatchGrid({
  enabled = true,
  enableStream = true,
}: { enabled?: boolean; enableStream?: boolean } = {}) {
  const request = useMemo(() => buildFedwatchRequest({ mode: 'grid' }), [])

  const query = useQuery({
    queryKey: fedwatchKeys.grid(FEDWATCH_EXCHANGE),
    queryFn: ({ signal }) => fetchFedwatchProbabilities(request!, signal),
    enabled: enabled && request !== null,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: retryUnlessBadRequest,
  })

  const [streamData, setStreamData] = useState<FedwatchProbabilityList>()

  useFedwatchStream({
    connected: enabled && enableStream,
    payload: useMemo(
      () => ({
        exchange: FEDWATCH_EXCHANGE,
        // Explicit here, unlike the pull's empty symbol: the inbound filter compares
        // this against each point's `currency`.
        symbol: FEDWATCH_SYMBOL,
        analyticsType: AnalyticsType.ANALYTICS_TYPE_FEDWATCH,
      }),
      []
    ),
    // A push is a FULL REPLACEMENT of the grid, so it is stored whole and never
    // merged. Routing this through the Amberdata path would be actively wrong:
    // `mergeAmberdataLists` keys rows by timestamp|currency|expiry|dte|timeframe,
    // which contains neither meetingDate nor targetLowerBp, so an entire grid would
    // collapse into one row per timestamp — a silent failure that looks like a
    // backend bug.
    onMessage: setStreamData,
  })

  return {
    ...query,
    data: pickNewerFedwatchList(streamData, query.data),
  }
}

/**
 * One meeting's probabilities over a window. Never streamed — a live push carries
 * the current grid and would overwrite a historical window.
 */
export function useFedwatchTimeline({
  meetingDate,
  fromDateMs,
  toDateMs,
  enabled = true,
}: {
  meetingDate: string
  fromDateMs: number | null
  toDateMs: number | null
  enabled?: boolean
}) {
  // Both bounds land on UTC day edges, so they are stable within a day and the query
  // key doesn't churn on every render.
  const { fromMs, toMs } = useMemo(
    () => resolveFedwatchRange(fromDateMs, toDateMs),
    [fromDateMs, toDateMs]
  )
  const from = String(fromMs)
  const to = String(toMs)
  const request = useMemo(
    () => buildFedwatchRequest({ mode: 'timeline', meetingDate, from, to }),
    [meetingDate, from, to]
  )

  return useQuery({
    queryKey: fedwatchKeys.timeline(FEDWATCH_EXCHANGE, meetingDate, from, to),
    queryFn: ({ signal }) => fetchFedwatchProbabilities(request!, signal),
    // `request` is null when the meeting date isn't a real calendar date, which the
    // server requires alongside from/to.
    enabled: enabled && request !== null,
    // No point re-asking inside a bucket that cannot have changed.
    staleTime: fedwatchBucketMsForSpan(toMs - fromMs),
    retry: retryUnlessBadRequest,
  })
}
